import React, { useState, useMemo } from "react";
import { useData, save } from "../hooks/useData";
import { toast } from "sonner";
import { PageHeader, DataTable, Button, Modal, Field, Input, Textarea, Select, StatusBadge } from "../components/shared";
import { api, num, fmtDate } from "../lib/api";
import { AlertTriangle, Layers } from "lucide-react";

export default function ProcessReceiving() {
  const { data, loading, reload } = useData("/process-issues");
  const { data: receipts } = useData("/process-receipts");
  const { data: orders } = useData("/production-orders");
  const { data: merges, reload: reloadMerges } = useData("/merges");
  const { data: jobbers } = useData("/jobbers");
  const [fwd, setFwd] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [issue, setIssue] = useState(null);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mForm, setMForm] = useState(null);

  const pending = (data || []).filter((i) => ["With Jobber", "Issued", "Partially Received"].includes(i.status));

  // Received parts grouped by production order for merging
  const partGroups = useMemo(() => {
    const parts = (data || []).filter((i) => i.is_part && !i.merged && (i.received_qty || 0) > 0);
    const map = {};
    for (const p of parts) {
      (map[p.production_order_id] = map[p.production_order_id] || []).push(p);
    }
    return map;
  }, [data]);

  const openReceive = (i) => {
    setIssue(i);
    const remaining = i.total_qty - (i.received_qty || 0) - (i.rejected_qty || 0);
    const joinsHere = i.is_part && (data || []).filter((p) => p.is_part && !p.merged && p.production_order_id === i.production_order_id && p.to_process === i.to_process).length >= 2;
    setForm({ issue_id: i.id, as_garment: joinsHere, received_qty: remaining, rejected_qty: 0, full_close: true, actual_receiving_date: new Date().toISOString().slice(0, 10), jobber_bill_number: "", rate: i.issue_rate || "", remarks: "" });
    setOpen(true);
  };

  const remaining = issue ? issue.total_qty - (issue.received_qty || 0) - (issue.rejected_qty || 0) : 0;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await save("post", "/process-receipts", { ...form, received_qty: parseFloat(form.received_qty) || 0, rejected_qty: parseFloat(form.rejected_qty) || 0 });
      toast.success("Received successfully");
      setOpen(false); reload();
    } catch {}
  };

  // ---- Send a received part onward ----
  const openForward = (p) => {
    const o = (orders || []).find((x) => x.id === p.production_order_id);
    const route = (o?.route || []).filter((r) => r !== p.to_process);
    setFwd({ part: p, route, to_process: route.includes("Stitching") ? "Stitching" : (route[0] || ""), mode: "Jobber", jobber_id: "",
      total_qty: p.received_qty || 0, issue_rate: "", issue_date: new Date().toISOString().slice(0, 10), expected_receiving_date: "" });
  };

  const submitForward = async (e) => {
    e.preventDefault();
    if (fwd.mode === "Jobber" && !fwd.jobber_id) { toast.error("Select a jobber"); return; }
    const j = (jobbers || []).find((x) => x.id === fwd.jobber_id);
    try {
      const res = await save("post", "/process-issues", {
        forward_from_issue_id: fwd.part.id, to_process: fwd.to_process, mode: fwd.mode, jobber_id: fwd.jobber_id,
        jobber_name: j?.name || "", total_qty: parseFloat(fwd.total_qty) || 0, sizes: fwd.part.sizes || [],
        issue_rate: fwd.issue_rate, issue_date: fwd.issue_date, expected_receiving_date: fwd.expected_receiving_date,
        generate_challan: fwd.mode === "Jobber",
      });
      toast.success(res.challan_number ? `${fwd.part.part_name} sent to ${fwd.to_process} — Challan ${res.challan_number}` : `${fwd.part.part_name} sent to ${fwd.to_process}`);
      setFwd(null); reload();
    } catch {}
  };

  // ---- Merge parts ----
  const openMerge = (oid) => {
    const o = (orders || []).find((x) => x.id === oid);
    const parts = partGroups[oid] || [];
    const suggestedQty = Math.max(0, ...parts.map((p) => p.received_qty || 0));
    setMForm({
      production_order_id: oid,
      order: o,
      parts,
      selected: parts.map((p) => p.id),
      to_process: (o?.route || []).find((r) => r !== "Cutting") || "",
      quantity: suggestedQty || "",
      date: new Date().toISOString().slice(0, 10),
    });
    setMergeOpen(true);
  };

  const togglePart = (id) => setMForm((f) => ({ ...f, selected: f.selected.includes(id) ? f.selected.filter((x) => x !== id) : [...f.selected, id] }));

  const submitMerge = async (e) => {
    e.preventDefault();
    if (mForm.selected.length < 2) { toast.error("Select at least two parts to merge"); return; }
    if (!mForm.to_process) { toast.error("Select the target process"); return; }
    if (!parseFloat(mForm.quantity)) { toast.error("Enter the merged garment quantity"); return; }
    try {
      const res = await api.post(`/production-orders/${mForm.production_order_id}/merge-parts`, {
        issue_ids: mForm.selected, to_process: mForm.to_process, quantity: parseFloat(mForm.quantity), date: mForm.date,
      });
      toast.success(`Merged into ${mForm.to_process} — ${num(res.data.quantity)} garments (${res.data.code})`);
      setMergeOpen(false); reload(); reloadMerges();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed");
    }
  };

  const columns = [
    { key: "challan_number", header: "Challan", mono: true, render: (r) => r.challan_number || "In-House" },
    { key: "production_code", header: "Prod Order", mono: true },
    { key: "part_name", header: "Part", render: (r) => r.is_part ? <span className="text-violet-700 font-medium">{r.part_name}</span> : <span className="text-slate-400">Full</span> },
    { key: "to_process", header: "Process" },
    { key: "jobber_name", header: "Jobber", render: (r) => r.jobber_name || "-" },
    { key: "issue_date", header: "Issued", render: (r) => fmtDate(r.issue_date) },
    { key: "expected_receiving_date", header: "Expected", render: (r) => fmtDate(r.expected_receiving_date) },
    { key: "total_qty", header: "Issue Qty", align: "right", render: (r) => num(r.total_qty) },
    { key: "received_qty", header: "Received", align: "right", render: (r) => num(r.received_qty) },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", header: "", sortable: false, render: (r) => <Button size="sm" onClick={(e) => { e.stopPropagation(); openReceive(r); }} data-testid={`receive-btn-${r.id}`}>Receive</Button> },
  ];

  const recCols = [
    { key: "actual_receiving_date", header: "Receive Date", render: (r) => fmtDate(r.actual_receiving_date) },
    { key: "challan_number", header: "Challan", mono: true },
    { key: "process", header: "Process" },
    { key: "jobber_name", header: "Jobber" },
    { key: "issue_qty", header: "Issued", align: "right", render: (r) => num(r.issue_qty) },
    { key: "received_qty", header: "Received", align: "right", render: (r) => num(r.received_qty) },
    { key: "amount", header: "Amount", align: "right", render: (r) => `₹${num(r.amount)}` },
  ];

  const mergeCols = [
    { key: "date", header: "Date", render: (r) => fmtDate(r.date) },
    { key: "code", header: "Merge No", mono: true },
    { key: "design_number", header: "Design" },
    { key: "part_names", header: "Parts Merged", render: (r) => (r.part_names || []).join(" + ") },
    { key: "to_process", header: "Into Process" },
    { key: "quantity", header: "Garment Qty", align: "right", render: (r) => <span className="font-semibold text-emerald-700">{num(r.quantity)}</span> },
  ];

  const partOrderIds = Object.keys(partGroups);

  return (
    <div>
      <PageHeader title="Process Receiving" subtitle="Receive goods back from jobber / in-house. Short qty auto-calculated." />
      <p className="text-sm font-semibold text-slate-700 mb-2">Pending Receipts ({pending.length})</p>
      {!loading && <DataTable testid="pending-receipts-table" columns={columns} rows={pending} exportName="pending-receipts.csv" />}

      {/* Merge parts section */}
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-2">
          <Layers size={16} className="text-violet-700" />
          <p className="text-sm font-semibold text-slate-700">Merge Parts into Garment</p>
        </div>
        <p className="text-xs text-slate-500 mb-3">Received parts (e.g. Front + Back + Sleeve) are merged back into the complete garment. The merged quantity is the garment count — not the sum of parts. Use <b>Send onward</b> to send a returned part (e.g. printed Fronts) to the jobber who has the other parts.</p>
        {partOrderIds.length === 0 ? (
          <div className="text-sm text-slate-400 border border-dashed rounded-md py-6 text-center" data-testid="no-parts-to-merge">No received parts pending merge.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {partOrderIds.map((oid) => {
              const parts = partGroups[oid];
              const o = (orders || []).find((x) => x.id === oid);
              return (
                <div key={oid} className="border rounded-lg p-4 bg-white" data-testid={`part-group-${oid}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-semibold text-slate-800">{o?.design_number || parts[0]?.design_number}</p>
                      <p className="text-xs text-slate-500">{parts[0]?.production_code} · {o?.colour}</p>
                    </div>
                    <StatusBadge status={`${parts.length} parts`} />
                  </div>
                  <ul className="text-xs text-slate-600 space-y-1 mb-3">
                    {parts.map((p) => (
                      <li key={p.id} className="flex justify-between">
                        <span className="text-violet-700 font-medium">{p.part_name || "Part"}</span>
                        <span className="flex items-center gap-2">
                          {p.jobber_name || "In-House"} · {p.to_process} · rec {num(p.received_qty)}
                          <button type="button" className="text-blue-700 hover:underline font-medium" onClick={() => openForward(p)} data-testid={`part-forward-${p.id}`}>Send onward</button>
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" onClick={() => openMerge(oid)} disabled={parts.length < 2} data-testid={`merge-open-${oid}`}>
                    <Layers size={14} /> Merge Parts
                  </Button>
                  {parts.length < 2 && <p className="text-[11px] text-slate-400 mt-1">Need 2+ received parts to merge.</p>}
                </div>
              );
            })}
          </div>
        )}
        {(merges || []).length > 0 && (
          <div className="mt-6">
            <p className="text-sm font-semibold text-slate-700 mb-2">Merge History</p>
            <DataTable testid="merge-history-table" columns={mergeCols} rows={merges || []} exportName="merges.csv" dense />
          </div>
        )}
      </div>

      <div className="mt-8">
        <p className="text-sm font-semibold text-slate-700 mb-2">Receipt History</p>
        <DataTable testid="receipts-history-table" columns={recCols} rows={receipts || []} exportName="receipts.csv" dense />
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={`Receive — ${issue?.challan_number || issue?.production_code}`}>
        {form && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-slate-50 rounded-md p-3 text-sm grid grid-cols-3 gap-2">
              <div><p className="text-xs text-slate-500">Process</p><p className="font-medium">{issue.to_process}</p></div>
              <div><p className="text-xs text-slate-500">{issue.is_part ? "Part" : "Issued Qty"}</p><p className="font-medium">{issue.is_part ? issue.part_name : num(issue.total_qty)}</p></div>
              <div><p className="text-xs text-slate-500">Remaining</p><p className="font-medium">{num(remaining)}</p></div>
            </div>
            {issue.is_part && (
              <label className="col-span-2 flex items-start gap-2 text-sm bg-violet-50 border border-violet-100 rounded-md px-3 py-2" data-testid="rec-as-garment-label">
                <input type="checkbox" className="mt-1" checked={!!form.as_garment} onChange={(e) => setForm({ ...form, as_garment: e.target.checked })} data-testid="rec-as-garment" />
                <span><b>Complete garments received</b> — all parts were joined at this jobber. The quantity moves into {issue.to_process} WIP and the lot's other open parts are closed.</span>
              </label>
            )}
            <Field label="Received Qty (full)"><div className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center tabular font-semibold text-emerald-700" data-testid="rec-qty">{num(remaining)}</div></Field>
            <Field label="Actual Receive Date"><Input type="date" value={form.actual_receiving_date} onChange={(e) => setForm({ ...form, actual_receiving_date: e.target.value })} /></Field>
            <Field label="Jobber Bill No"><Input value={form.jobber_bill_number} onChange={(e) => setForm({ ...form, jobber_bill_number: e.target.value })} /></Field>
            <Field label="Rate"><Input type="number" step="any" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></Field>
            <Field label="Remarks" className="col-span-2"><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="rec-save">Confirm Receipt</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!fwd} onClose={() => setFwd(null)} title={`Send ${fwd?.part?.part_name || "part"} onward`}>
        {fwd && (
          <form onSubmit={submitForward} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-slate-50 rounded-md p-3 text-sm">
              <p className="font-medium">{fwd.part.design_number} · {fwd.part.part_name}</p>
              <p className="text-xs text-slate-500">Back from {fwd.part.to_process} ({fwd.part.jobber_name || "In-House"}) · received {num(fwd.part.received_qty)}</p>
            </div>
            <Field label="Send To Process" required>
              <Select value={fwd.to_process} onChange={(e) => setFwd({ ...fwd, to_process: e.target.value })} required options={fwd.route.map((r) => ({ value: r, label: r }))} data-testid="fwd-to-process" />
            </Field>
            <Field label="Mode"><Select value={fwd.mode} onChange={(e) => setFwd({ ...fwd, mode: e.target.value })} options={["Jobber", "In-House"]} data-testid="fwd-mode" /></Field>
            {fwd.mode === "Jobber" && (
              <Field label="Jobber" required>
                <Select value={fwd.jobber_id} onChange={(e) => setFwd({ ...fwd, jobber_id: e.target.value })} required placeholder="Select jobber" options={(jobbers || []).map((j) => ({ value: j.id, label: j.name }))} data-testid="fwd-jobber" />
              </Field>
            )}
            <Field label="Quantity" required><Input type="number" step="any" value={fwd.total_qty} onChange={(e) => setFwd({ ...fwd, total_qty: e.target.value })} required data-testid="fwd-qty" /></Field>
            <Field label="Rate"><Input type="number" step="any" value={fwd.issue_rate} onChange={(e) => setFwd({ ...fwd, issue_rate: e.target.value })} data-testid="fwd-rate" /></Field>
            <Field label="Issue Date"><Input type="date" value={fwd.issue_date} onChange={(e) => setFwd({ ...fwd, issue_date: e.target.value })} /></Field>
            <Field label="Expected Return"><Input type="date" value={fwd.expected_receiving_date} onChange={(e) => setFwd({ ...fwd, expected_receiving_date: e.target.value })} /></Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setFwd(null)}>Cancel</Button>
              <Button type="submit" data-testid="fwd-save">Send &amp; Create Challan</Button>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={mergeOpen} onClose={() => setMergeOpen(false)} title="Merge Parts into Complete Garment">
        {mForm && (
          <form onSubmit={submitMerge} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-violet-50 border border-violet-100 rounded-md p-3 text-sm">
              <p className="font-semibold text-slate-800">{mForm.order?.design_number} · {mForm.order?.colour}</p>
              <p className="text-xs text-slate-500">Select the parts to combine. The merged quantity is the number of complete garments, not the sum of parts.</p>
            </div>
            <div className="col-span-2 space-y-2">
              {mForm.parts.map((p) => (
                <label key={p.id} className="flex items-center justify-between gap-3 border rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50" data-testid={`merge-part-${p.id}`}>
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={mForm.selected.includes(p.id)} onChange={() => togglePart(p.id)} data-testid={`merge-part-cb-${p.id}`} />
                    <span className="text-violet-700 font-medium">{p.part_name || "Part"}</span>
                    <span className="text-xs text-slate-500">{p.jobber_name || "In-House"}</span>
                  </span>
                  <span className="text-xs text-slate-600">received {num(p.received_qty)}</span>
                </label>
              ))}
            </div>
            <Field label="Merge Into Process" required>
              <Select value={mForm.to_process} onChange={(e) => setMForm({ ...mForm, to_process: e.target.value })} required placeholder="Select process"
                options={(mForm.order?.route || []).map((r) => ({ value: r, label: r }))} data-testid="merge-to-process" />
            </Field>
            <Field label="Complete Garment Qty" required>
              <Input type="number" step="any" value={mForm.quantity} onChange={(e) => setMForm({ ...mForm, quantity: e.target.value })} required data-testid="merge-qty" />
            </Field>
            <div className="col-span-2 flex items-center gap-2 text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2">
              <AlertTriangle size={14} className="text-amber-500" /> {mForm.selected.length} parts selected → <b className="mx-1">{num(parseFloat(mForm.quantity) || 0)}</b> complete garments added to <b className="ml-1">{mForm.to_process || "…"}</b>
            </div>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => setMergeOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="merge-save"><Layers size={14} /> Confirm Merge</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
